import getEnv from '@shared/utils/env';
import type { LocationQuery } from 'vue-router';

/**
 * Origins the portal may send a person back to: the application's own and any configured in
 * VUE_APP_ALLOWED_RETURN_ORIGINS, a comma-separated list. Paths on the portal itself are always
 * allowed.
 */
function allowedOrigins(): string[] {
  return [getEnv('VUE_APP_APP_URL'), ...(getEnv('VUE_APP_ALLOWED_RETURN_ORIGINS') || '').split(',')]
    .map((value) => {
      try {
        return new URL((value || '').trim()).origin;
      } catch {
        return null;
      }
    })
    .filter((origin): origin is string => origin !== null && origin !== 'null');
}

/** Backslashes and control characters make browsers and URL parsers disagree. */
function unsafeCharacter(character: string): boolean {
  const code = character.charCodeAt(0);
  return character === '\\' || code < 0x20 || code === 0x7f;
}

/** The return address when it is safe to follow, or `null`; never an arbitrary site. */
export function safeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const candidate = value.trim();
  if (candidate === '' || [...candidate].some(unsafeCharacter)) {
    return null;
  }
  if (candidate.startsWith('/') && !candidate.startsWith('//')) {
    return candidate;
  }
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (!['http:', 'https:'].includes(url.protocol)) {
    return null;
  }
  return allowedOrigins().includes(url.origin) ? url.toString() : null;
}

/** Keeps a page's `returnTo` when moving to another portal page. */
export function carriedReturnTo(query: LocationQuery): { returnTo?: string } {
  return typeof query.returnTo === 'string' ? { returnTo: query.returnTo } : {};
}
